// libs
import { getCookie } from '@nykaa/utils/cookies';
// constants
import { NYKAA_ANALYTICS_CACHE_KEY, NYKAA_VISITOR_ID_COOKIE, OMNITURE_MC_ID_KEY, ANALYTICS_PAGE_COUNTER, ANALYTICS_EVENT_COUNTER, ANALYTICS_SESSION_UPDATED_AT, ANALYTICS_EVENT_TIME, ANALYTICS_SESSION_ID, } from '../constants';
/**
 * Get omniture mcid from marketingCloudVisitorID
 * Also, save it to localStorage for further usage
 * In case marketingCloudVisitorIDis not present of subsequent navigations, we can use localStorage copy, if available
 * @returns {string} omniture MCID
 * @returns null in case omniture MCID is not set, also not found in localStorage
 */
export const getOmnitureMCID = () => {
    var _a;
    let mcID = (_a = window === null || window === void 0 ? void 0 : window.s) === null || _a === void 0 ? void 0 : _a.marketingCloudVisitorID;
    try {
        if (window && window.localStorage) {
            if (mcID) {
                window.localStorage.setItem(OMNITURE_MC_ID_KEY, mcID);
            }
            else {
                mcID = window.localStorage.getItem(OMNITURE_MC_ID_KEY);
            }
        }
        // eslint-disable-next-line no-empty
    }
    catch (err) { }
    return mcID || null;
};
/**
 * Get visitor id from document cookies' "bcookie"
 * @returns {string} visitor id
 */
export const getVisitorId = () => getCookie(NYKAA_VISITOR_ID_COOKIE);
/**
 * Get Browser network type
 * @returns {string} Network type - 'wifi', 'cellular', etc.
 */
// @ts-ignore - connection is not defined in lib.dom
export const getBrowserNetworkType = () => { var _a, _b; return ((_b = (_a = window === null || window === void 0 ? void 0 : window.navigator) === null || _a === void 0 ? void 0 : _a.connection) === null || _b === void 0 ? void 0 : _b.type) || 'NA'; };
/**
 * Get Browser effective network type
 * @returns {string} Network effective type - 'slow-2g', '2g', '3g', or '4g'.
 */
// @ts-ignore - connection is not defined in lib.dom
export const getBrowserNetworkSpeed = () => { var _a, _b; return ((_b = (_a = window === null || window === void 0 ? void 0 : window.navigator) === null || _a === void 0 ? void 0 : _a.connection) === null || _b === void 0 ? void 0 : _b.effectiveType) || 'NA'; };
/**
 * Store events in localStorage
 * @param {object} data - data containing list of events and commonFields
 */
export const storeAnalytics = (data) => {
    try {
        window.localStorage.setItem(NYKAA_ANALYTICS_CACHE_KEY, JSON.stringify(data));
    }
    catch (e) {
        /** DO NOTHING - as localStorage error */
    }
};
/**
 * Clear all data stored in localStorage
 */
export const clearAnalytics = () => {
    try {
        window.localStorage.removeItem(NYKAA_ANALYTICS_CACHE_KEY);
    }
    catch (e) {
        /** DO NOTHING - as localStorage error */
    }
};
/**
 * Get all events stored in localStorage
 * @returns {object} events list and commonFields
 */
export const getStoredAnalytics = () => {
    try {
        const savedData = window.localStorage.getItem(NYKAA_ANALYTICS_CACHE_KEY);
        if (!savedData) {
            return { events: [], commonFields: {} };
        }
        const { events = [], commonFields = {} } = JSON.parse(savedData);
        return { events, commonFields };
    }
    catch (e) {
        /** DO NOTHING - as localStorage error */
        return { events: [], commonFields: {} };
    }
};
/**
 * Util function to remove shallow undefined keys
 * @param {object} obj - any object
 * @returns {object}
 */
export const removeEmptyKeys = (obj = {}) => {
    const newObject = {};
    Object.keys(obj).forEach((key) => {
        if (obj[key] === undefined) {
            return;
        }
        newObject[key] = obj[key];
    });
    return newObject;
};
/**
 * Util function to get value of key from localStorage
 * @param {string} key - key in LS
 * @param {string | number} defaultValue - default value in case of error
 * @returns {string | number | undefined} - different as per the param
 */
export const getFromLS = (key, defaultValue = 0) => {
    try {
        return window.localStorage.getItem(key) || defaultValue;
    }
    catch (err) {
        return defaultValue;
    }
};
/**
 * Util function to set value of key in localStorage
 * @param {string} key - key in LS
 * @param {string | number} value - value to save in LS
 * @returns {void} - as nothing is needed to return
 */
export const setToLS = (key, value) => {
    try {
        if (!key)
            return;
        window.localStorage.setItem(key, `${value}`);
    }
    catch (err) {
        // do nothing
    }
};
/**
  =Util function to set data in localStorage in batch
  * @param {string} dataSet - key value pair for array of objects in LS
  * @returns {void} - as nothing is needed to return
*/
export const updateBatchRecordsToLS = (dataSet) => {
    try {
        if (dataSet.length <= 0)
            return;
        dataSet.forEach((currData) => {
            setToLS(currData === null || currData === void 0 ? void 0 : currData.key, currData === null || currData === void 0 ? void 0 : currData.value);
        });
    }
    catch (error) {
        // do nothing
    }
};
/**
 * Util function to get object for session event data
 * @param {Record<string, any>} eventData - eventObject
 * @param {string | undefined} pageName - pageName
 * @param {string | undefined} pageType - pageType
 * @param {string | number} sessionId - sessionId
 * @param {string | number} sessionPageId - sessionPageId
 * @param {string | number} sessionEventId - sessionEventId
 * @returns {Record<string, any>} - return a object with the updated keys with value
 */
export const getEventSessionData = (eventData, pageName, pageType, sessionId, sessionPageId, sessionEventId) => (Object.assign(Object.assign({}, eventData), { page_name: (eventData === null || eventData === void 0 ? void 0 : eventData.page_name) || pageName || '', page_type: (eventData === null || eventData === void 0 ? void 0 : eventData.page_type) || pageType || '', session_id: sessionId, session_page_id: sessionPageId, session_event_id: sessionEventId }));
/**
 * Util function to modify event for new session
 * @param {Record<string, any>} eventData
 * @param {string | number} sessionId
 * @param {boolean} isPageView
 * @param {number} currentTime
 * @param {string | undefined} localPageName
 * @param {string | undefined} localPageType
 * @returns {void} - as nothing is needed to return
 */
export const getEventForNewSession = (eventData, sessionId, isPageView, currentTime, localPageName, localPageType) => {
    updateBatchRecordsToLS([{
            key: ANALYTICS_PAGE_COUNTER, value: isPageView ? 1 : 0,
        }, {
            key: ANALYTICS_EVENT_COUNTER, value: 1,
        }, {
            key: ANALYTICS_SESSION_UPDATED_AT, value: currentTime,
        }, {
            key: ANALYTICS_EVENT_TIME, value: currentTime,
        }, {
            key: ANALYTICS_SESSION_ID, value: sessionId,
        }]);
    return getEventSessionData(eventData, localPageName, localPageType, sessionId, 1, 1);
};
/**
 * Util function to modify event for same session
 * @param {Record<string, any>} eventData
 * @param {string | number} sessionId
 * @param {boolean} isPageView
 * @param {number} currentTime
 * @param {string | undefined} localPageName
 * @param {string | undefined} localPageType
 * @param {number} pageCounter
 * @param {number} eventCounter
 * @returns {void} - as nothing is needed to return
 */
export const getEventForSameSession = (eventData, sessionId, isPageView, currentTime, localPageName, localPageType, pageCounter, eventCounter) => {
    if (isPageView) {
        updateBatchRecordsToLS([{
                key: ANALYTICS_PAGE_COUNTER, value: pageCounter + 1,
            }, {
                key: ANALYTICS_EVENT_TIME, value: currentTime,
            }]);
        return getEventSessionData(eventData, localPageName, localPageType, sessionId, pageCounter + 1, eventCounter + 1);
    }
    updateBatchRecordsToLS([{
            key: ANALYTICS_EVENT_COUNTER, value: eventCounter + 1,
        }, {
            key: ANALYTICS_EVENT_TIME, value: currentTime,
        }]);
    return getEventSessionData(eventData, localPageName, localPageType, sessionId, pageCounter, eventCounter + 1);
};
/**
 * Util function to get Active ABConfig event from datalayer and modify it
 * @returns {object} - returns active abConfig event object
 */
export const getDataForABConfig = () => {
    const { datalayer = {} } = window;
    const abConfig = (datalayer === null || datalayer === void 0 ? void 0 : datalayer.ABV2) || {};
    let activeABs = {};
    try {
        activeABs = abConfig.filter((ele) => {
            const exp = ele[Object.keys(ele)[0]] || {};
            const isExpEligible = (exp === null || exp === void 0 ? void 0 : exp.isEligible) || false;
            const isExpVariant = !!(exp === null || exp === void 0 ? void 0 : exp.variant);
            return !!(isExpEligible && isExpVariant);
        });
    }
    catch (error) {
        activeABs = {};
    }
    return activeABs;
};
/**
 * Util function to get currentTimestamp for event object
 * @returns {object} - returns currentTimestamp
 */
export const getCurrentTimeStampForEvent = () => new Date().getTime() / 1000;
/**
 * Util function to check if the category page is search redirected
 * @returns {boolean} - returns true if url params contains True, otherwise it's false
 */
export const isSearchRedirectedPLP = () => {
    var _a;
    let status = false;
    try {
        const urlParams = new URLSearchParams(((_a = window === null || window === void 0 ? void 0 : window.location) === null || _a === void 0 ? void 0 : _a.search) || '');
        const searchRedirectionParam = urlParams.get('search_redirection');
        status = !!(searchRedirectionParam === 'True');
    }
    catch (error) {
        status = false;
    }
    return status;
};
